// frontend/src/pages/LaporYuk.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { useParams } from "react-router-dom";
import { authHeaders } from "../utils/authHeaders";
import "./LaporYuk.css";

const API = (process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");

async function asJsonOrThrow(res) {
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    const t = await res.text();
    throw new Error(`Unexpected response (not JSON): ${t.slice(0, 140)}...`);
  }
  const j = await res.json();
  if (!res.ok || !j.success) throw new Error(j?.message || `HTTP ${res.status}`);
  return j;
}

function tryParseJson(v) {
  if (v == null) return null;
  if (typeof v === "object") return v;
  if (typeof v === "string") {
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  }
  return null;
}

function tryParseArray(v) {
  const j = tryParseJson(v);
  return Array.isArray(j) ? j : [];
}

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function isFileValue(v) {
  return typeof File !== "undefined" && v instanceof File;
}

function isGpsValue(v) {
  return (
    v &&
    typeof v === "object" &&
    Number.isFinite(Number(v.latitude)) &&
    Number.isFinite(Number(v.longitude))
  );
}

function formatGpsValue(v) {
  if (!isGpsValue(v)) return "";
  const lat = Number(v.latitude).toFixed(6);
  const lng = Number(v.longitude).toFixed(6);
  const accuracy = Number.isFinite(Number(v.accuracy))
    ? `, akurasi ${Math.round(Number(v.accuracy))} m`
    : "";
  return `${lat}, ${lng}${accuracy}`;
}

// ===== formula evaluator (Q1 + Q2 * 10) =====
function evalFormulaSafe(formula, answers) {
  if (!formula || typeof formula !== "string") return null;

  // ganti Q1/Q2 dst dengan angka
  let expr = formula.replace(/Q(\d+)/gi, (_, qNoStr) => {
    const qNo = Number(qNoStr);
    const a = answers[String(qNo)];
    const n = safeNumber(a);
    return n == null ? "0" : String(n);
  });

  // allowlist karakter aman
  // hanya angka, spasi, + - * / ( ) . 
  if (!/^[0-9+\-*/().\s]+$/.test(expr)) return null;

  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(`return (${expr});`);
    const out = fn();
    const n = safeNumber(out);
    return n == null ? null : n;
  } catch {
    return null;
  }
}

// =========================
// Logic evaluator (SHOW/HIDE/CALCULATE)
// =========================
function getLogicArray(q) {
  const raw = q?.logic ?? q?.logics ?? q?.rules;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") return raw; // bisa array/object
  if (typeof raw === "string") return tryParseArray(raw);
  return [];
}

function getConditionQuestionNo(cond, questions = []) {
  const key = cond?.question_key;
  if (key) {
    const idx = questions.findIndex((q) => String(q.client_key || "") === String(key));
    if (idx >= 0) return idx + 1;
  }
  return Number(cond?.question_id);
}

function evalCondition(cond, getAnswer, questions = []) {
  const qNo = getConditionQuestionNo(cond, questions);
  const op = cond?.operator || "equals";
  const expected = cond?.value;

  const actual = getAnswer(qNo);

  const asNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  if (op === "equals") {
    if (Array.isArray(actual)) return actual.includes(String(expected));
    return String(actual ?? "") === String(expected ?? "");
  }

  if (op === "not_equals") {
    if (Array.isArray(actual)) return !actual.includes(String(expected));
    return String(actual ?? "") !== String(expected ?? "");
  }

  if (op === "contains") {
    if (Array.isArray(actual)) return actual.includes(String(expected));
    return String(actual ?? "").toLowerCase().includes(String(expected ?? "").toLowerCase());
  }

  if (op === "greater") {
    const a = asNum(actual);
    const b = asNum(expected);
    if (a === null || b === null) return false;
    return a > b;
  }

  if (op === "less") {
    const a = asNum(actual);
    const b = asNum(expected);
    if (a === null || b === null) return false;
    return a < b;
  }

  return false;
}

function computeVisibleForQuestion(q, getAnswer, questions = []) {
  const rules = getLogicArray(q);

  const showRules = rules.filter((r) => r?.then?.action === "show");
  const hideRules = rules.filter((r) => r?.then?.action === "hide");

  // Default:
  // - kalau ada SHOW rule -> default hidden sampai ada yang TRUE
  // - kalau tidak ada SHOW rule -> default visible
  let visible = showRules.length > 0 ? false : true;

  if (showRules.length > 0) {
    visible = showRules.some((r) => evalCondition(r.if, getAnswer, questions));
  }

  // Hide rule prioritas
  if (hideRules.some((r) => evalCondition(r.if, getAnswer, questions))) {
    visible = false;
  }

  return visible;
}

export default function LaporYuk() {
  const { user, token } = useContext(AuthContext);
  const role = String(user?.role || "").trim().toLowerCase();
  const isFieldTeam = role === "field team";
  const { surveyId: sharedSurveyIdParam } = useParams();
  const sharedSurveyId = Number(sharedSurveyIdParam);
  const isShareMode = Number.isFinite(sharedSurveyId) && sharedSurveyId > 0;

  const [loading, setLoading] = useState(true);
  const [published, setPublished] = useState([]); // rows dari survey_versions (latest per survey)
  const [active, setActive] = useState(null); // {survey(version), questions(version_questions)}
  const [answers, setAnswers] = useState({}); // key: "QNo" -> value
  const [submitting, setSubmitting] = useState(false);
  const [locatingQuestion, setLocatingQuestion] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [assistedUsers, setAssistedUsers] = useState([]);
  const [assistedUserId, setAssistedUserId] = useState("");

  useEffect(() => {
    if (!user?.id || !isFieldTeam) return;

    fetch(`${API}/user/sahabat-kakao`, {
      headers: authHeaders(user, { Accept: "application/json" }),
      credentials: "include",
    })
      .then(asJsonOrThrow)
      .then((json) => {
        const rows = json.data || [];
        setAssistedUsers(rows);
        setAssistedUserId((current) => current || (rows[0]?.id ? String(rows[0].id) : ""));
      })
      .catch((err) => {
        console.error("Gagal memuat akun dampingan:", err);
        setAssistedUsers([]);
      });
  }, [user, isFieldTeam]);

  const assistedUser = useMemo(
    () => assistedUsers.find((item) => Number(item.id) === Number(assistedUserId)) || null,
    [assistedUsers, assistedUserId]
  );

  const loadSurveyById = async (surveyId) => {
    const res = await fetch(`${API}/surveys/published/${surveyId}`, {
      headers: authHeaders(user, { Accept: "application/json" }),
      credentials: "include",
    });
    const json = await asJsonOrThrow(res);

    const sortedQ = (json.questions || [])
      .slice()
      .sort((a, b) => Number(a.q_order) - Number(b.q_order));

    setAnswers({});
    setLocatingQuestion(null);
    setSubmitted(false);
    setActive({
      surveyId,
      versionId: json.survey?.id,
      survey: json.survey,
      questions: sortedQ,
    });
  };

  // ============ Load list published ============
  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError("");
      setActive(null);
      setPublished([]);
      try {
        if (isShareMode) {
          setPublished([]);
          await loadSurveyById(sharedSurveyId);
          return;
        }

        setActive(null);
        setSubmitted(false);
        const res = await fetch(`${API}/surveys/published`, {
          headers: authHeaders(user, { Accept: "application/json" }),
          credentials: "include",
        });
        const json = await asJsonOrThrow(res);
        setPublished(json.data || []);
      } catch (e) {
        console.error("Load published surveys error:", e);
        setLoadError(e.message || "Gagal memuat survey.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShareMode, sharedSurveyId, user?.id, role, token]);

  // ============ Open survey detail ============
  const openSurvey = async (sv) => {
    // sv adalah row dari survey_versions:
    // sv.id = survey_version_id
    // sv.survey_id = surveys.id  <-- ini yang benar untuk endpoint published/:surveyId
    const surveyId = sv?.survey_id;

    try {
      await loadSurveyById(surveyId);
    } catch (e) {
      console.error("Open survey error:", e);
      alert(e.message || "Gagal memuat detail survey.");
    }
  };

  const closeSurvey = () => {
    setActive(null);
    setAnswers({});
    setLocatingQuestion(null);
  };

  // ============ Answer helpers ============
  const getAnswer = (qNo) => answers[String(qNo)];

  const setAnswer = (qNo, value) => {
    setAnswers((prev) => ({ ...prev, [String(qNo)]: value }));
  };

  const toggleMulti = (qNo, optionValue) => {
    setAnswers((prev) => {
      const key = String(qNo);
      const cur = Array.isArray(prev[key]) ? prev[key] : [];
      const v = String(optionValue);
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      return { ...prev, [key]: next };
    });
  };

  const captureGps = (qNo) => {
    if (!navigator.geolocation) {
      alert("Browser/perangkat ini belum mendukung pengambilan lokasi GPS.");
      return;
    }

    setLocatingQuestion(qNo);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = pos.coords;
        setAnswer(qNo, {
          latitude: c.latitude,
          longitude: c.longitude,
          accuracy: c.accuracy,
          altitude: c.altitude,
          altitude_accuracy: c.altitudeAccuracy,
          heading: c.heading,
          speed: c.speed,
          captured_at: new Date(pos.timestamp || Date.now()).toISOString(),
        });
        setLocatingQuestion(null);
      },
      (err) => {
        setLocatingQuestion(null);
        const message =
          err.code === err.PERMISSION_DENIED
            ? "Izin lokasi ditolak. Aktifkan izin lokasi pada browser/perangkat."
            : err.code === err.TIMEOUT
            ? "Pengambilan lokasi terlalu lama. Coba lagi di area dengan sinyal GPS lebih baik."
            : "Gagal mengambil lokasi GPS. Pastikan GPS/lokasi perangkat aktif.";
        alert(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      }
    );
  };

  // ============ Visibility map ============
  const visibility = useMemo(() => {
    if (!active?.questions?.length) return {};
    const vis = {};
    active.questions.forEach((q, idx) => {
      const qNo = idx + 1; // QNo berbasis urutan di UI
      vis[qNo] = computeVisibleForQuestion(q, getAnswer, active.questions);
    });
    return vis;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, answers]);

  // ============ Auto clear hidden answers (lebih clean & aman submit) ============
  useEffect(() => {
    if (!active?.questions?.length) return;
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      active.questions.forEach((_, idx) => {
        const qNo = idx + 1;
        if (visibility[qNo] === false && next[String(qNo)] != null) {
          delete next[String(qNo)];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility, active?.questions?.length]);

  // ============ Auto calculation ============
  useEffect(() => {
    if (!active?.questions?.length) return;

    // 1) calculation field pada question
    // 2) calculate rules: {then:{action:'calculate', value:'Q1+Q2'}}
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };

      active.questions.forEach((q, idx) => {
        const qNo = idx + 1;

        // Skip jika hidden
        if (visibility[qNo] === false) return;

        // (A) calculate rules
        const rules = getLogicArray(q);
        const calcRules = rules.filter((r) => r?.then?.action === "calculate" && r?.then?.value);
        for (const r of calcRules) {
          if (evalCondition(r.if, (n) => next[String(n)], active.questions)) {
            const val = evalFormulaSafe(String(r.then.value), next);
            if (val != null) {
              if (String(next[String(qNo)] ?? "") !== String(val)) {
                next[String(qNo)] = val;
                changed = true;
              }
            }
            break; // ambil rule pertama yang match
          }
        }

        // (B) calculation field (default kalkulasi selalu berjalan)
        if (q?.calculation) {
          const val = evalFormulaSafe(String(q.calculation), next);
          if (val != null) {
            if (String(next[String(qNo)] ?? "") !== String(val)) {
              next[String(qNo)] = val;
              changed = true;
            }
          }
        }
      });

      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, visibility, answers]);

  // ============ Submit ============
  const submitSurvey = async () => {
    if (!active?.surveyId) return;
    if (isFieldTeam && !assistedUser) {
      alert("Pilih Sahabat Kakao yang akan dibantu mengirim laporan.");
      return;
    }

    // validasi required yang visible
    const missing = [];
    active.questions.forEach((q, idx) => {
      const qNo = idx + 1;
      if (visibility[qNo] === false) return;
      if (!q.required) return;

      const a = answers[String(qNo)];
      const empty =
        a == null ||
        (q.type === "gps" && !isGpsValue(a)) ||
        (typeof a === "string" && a.trim() === "") ||
        (Array.isArray(a) && a.length === 0);

      if (empty) missing.push(qNo);
    });

    if (missing.length) {
      alert(`Mohon isi pertanyaan wajib: ${missing.map((n) => `Q${n}`).join(", ")}`);
      return;
    }

    const mediaFiles = [];
    const respondent = isFieldTeam ? assistedUser : user;
    const payload = {
      respondent_user_id: respondent?.id ?? null,
      respondent_name: respondent?.full_name || respondent?.name || null,
      respondent_phone: respondent?.phone || respondent?.no_hp || null,
      answers: active.questions
        .map((q, idx) => {
          const qNo = idx + 1;
          if (visibility[qNo] === false) return null;

          const a = answers[String(qNo)];
          const fileField = isFileValue(a) ? `media_q_${qNo}` : null;
          if (fileField) mediaFiles.push({ field: fileField, file: a });

          // map field sesuai survey_answers:
          // - answer_text (string)
          // - answer_number (number)
          // - answer_json (array/object)
          let answer_text = null;
          let answer_number = null;
          let answer_json = null;

          if (fileField) {
            answer_text = a.name || null;
            answer_json = {
              original_name: a.name,
              size: a.size,
              mime_type: a.type,
            };
          } else if (Array.isArray(a) || (a && typeof a === "object")) {
            answer_json = a;
          } else if (q.type === "number" || q.type === "decimal" || q.type === "calculation") {
            const n = safeNumber(a);
            answer_number = n == null ? null : n;
          } else {
            answer_text = a == null ? null : String(a);
          }

          return {
            q_order: q.q_order ?? idx,
            question_key: q.client_key || null,
            file_field: fileField,
            answer_text,
            answer_number,
            answer_json,
          };
        })
        .filter(Boolean),
    };

    setSubmitting(true);
    try {
      const hasMedia = mediaFiles.length > 0;
      const body = hasMedia ? new FormData() : JSON.stringify(payload);
      if (hasMedia) {
        body.append("payload", JSON.stringify(payload));
        mediaFiles.forEach(({ field, file }) => body.append(field, file));
      }

      const res = await fetch(`${API}/surveys/published/${active.surveyId}/submit`, {
        method: "POST",
        headers: authHeaders(user, hasMedia
          ? { Accept: "application/json" }
          : { "Content-Type": "application/json", Accept: "application/json" }),
        credentials: "include",
        body,
      });
      const json = await asJsonOrThrow(res);
      alert("Terima kasih! Laporan Anda berhasil dikirim.");
      console.log("submission_id:", json.submission_id);
      if (isShareMode) {
        setSubmitted(true);
        setAnswers({});
      } else {
        closeSurvey();
      }
    } catch (e) {
      console.error("Submit error:", e);
      alert(`Gagal mengirim laporan: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const assistSelector = isFieldTeam ? (
    <div className="ly-assist-panel">
      <label htmlFor="lapor-assisted-user">Laporan untuk</label>
      <select
        id="lapor-assisted-user"
        value={assistedUserId}
        onChange={(e) => setAssistedUserId(e.target.value)}
      >
        {assistedUsers.length === 0 && <option value="">Belum ada Sahabat Kakao</option>}
        {assistedUsers.map((member) => (
          <option key={member.id} value={member.id}>{member.full_name}</option>
        ))}
      </select>
    </div>
  ) : null;

  // ============ Render question ============
  const renderQuestion = (q, idx) => {
    const qNo = idx + 1;
    if (visibility[qNo] === false) return null;

    const req = q.required ? " *" : "";
    const opts = q.options ? (Array.isArray(q.options) ? q.options : tryParseArray(q.options)) : [];
    const answer = getAnswer(qNo);

    const isCalculated = q.type === "calculation" || Boolean(q?.calculation) || getLogicArray(q).some((r) => r?.then?.action === "calculate");

    return (
      <div key={q.id || idx} className="ly-question">
        <div className="ly-qhead">
          <div className="ly-qtitle">
            {qNo}. {q.question_text}
            {req}
          </div>
          {q.type && <span className="ly-qtype">{q.type}</span>}
        </div>

        {q.type === "select_one" && (
          <div className="ly-choices">
            {opts.map((o, i) => (
              <label key={i} className="ly-choice">
                <input
                  type="radio"
                  name={`q${qNo}`}
                  checked={String(answer ?? "") === String(o)}
                  onChange={() => setAnswer(qNo, String(o))}
                />
                <span>{o}</span>
              </label>
            ))}
          </div>
        )}

        {q.type === "select_multiple" && (
          <div className="ly-choices">
            {opts.map((o, i) => (
              <label key={i} className="ly-choice">
                <input
                  type="checkbox"
                  checked={Array.isArray(answer) ? answer.includes(String(o)) : false}
                  onChange={() => toggleMulti(qNo, String(o))}
                />
                <span>{o}</span>
              </label>
            ))}
          </div>
        )}

        {q.type === "text" && (
          <input
            className="ly-field"
            type="text"
            placeholder="Jawaban Anda"
            value={answer ?? ""}
            disabled={isCalculated}
            onChange={(e) => setAnswer(qNo, e.target.value)}
          />
        )}

        {q.type === "number" && (
          <input
            className="ly-field"
            type="number"
            placeholder="Jawaban Anda"
            value={answer ?? ""}
            disabled={isCalculated}
            onChange={(e) => setAnswer(qNo, e.target.value)}
          />
        )}

        {q.type === "decimal" && (
          <input
            className="ly-field"
            type="number"
            step="0.01"
            placeholder="Jawaban Anda"
            value={answer ?? ""}
            disabled={isCalculated}
            onChange={(e) => setAnswer(qNo, e.target.value)}
          />
        )}

        {q.type === "date" && (
          <input
            className="ly-field"
            type="date"
            value={answer ?? ""}
            disabled={isCalculated}
            onChange={(e) => setAnswer(qNo, e.target.value)}
          />
        )}

        {q.type === "gps" && (
          <div className="ly-gps">
            <button
              type="button"
              className="ly-btnSoft"
              onClick={() => captureGps(qNo)}
              disabled={isCalculated || locatingQuestion === qNo}
            >
              {locatingQuestion === qNo ? "Mengambil lokasi..." : isGpsValue(answer) ? "Ambil Ulang Lokasi" : "Ambil Lokasi GPS"}
            </button>
            {isGpsValue(answer) ? (
              <div className="ly-gpsResult">
                <strong>Lokasi tersimpan</strong>
                <span>{formatGpsValue(answer)}</span>
              </div>
            ) : (
              <div className="ly-gpsHint">Tekan tombol untuk mengambil lokasi perangkat saat ini.</div>
            )}
          </div>
        )}

        {q.type === "calculation" && (
          <input
            className="ly-field"
            type="number"
            step="any"
            placeholder="Nilai dihitung otomatis"
            value={answer ?? ""}
            readOnly
            aria-label={`Hasil kalkulasi ${q.question_text}`}
          />
        )}

        {q.type === "media" && (
          <>
            <input
              className="ly-field"
              type="file"
              accept="image/*,video/*,application/pdf"
              onChange={(e) => setAnswer(qNo, e.target.files?.[0] || null)}
            />
            {isFileValue(answer) && <div className="ly-calcHint">File dipilih: {answer.name}</div>}
          </>
        )}

        {(q.type === "calculation" || q.calculation || getLogicArray(q).some((r) => r?.then?.action === "calculate")) && (
          <div className="ly-calcHint">
            Nilai dihitung otomatis.
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="lapor-yuk">
        <h1>Lapor Yuk!</h1>
        <p className="muted">Memuat survey...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="lapor-yuk">
        <div className="ly-head">
          <div>
            <h1>{isShareMode ? "Survey" : "Lapor Yuk!"}</h1>
            <p className="muted" role="alert">{loadError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isShareMode) {
    return (
      <div className="lapor-yuk ly-share">
        <div className="ly-head">
          <div>
            <h1>{active?.survey?.title || "Survey"}</h1>
            {active?.survey?.description ? (
              <p className="muted">{active.survey.description}</p>
            ) : (
              <p className="muted">Silakan isi survey berikut.</p>
            )}
          </div>
          {assistSelector}
        </div>

        {submitted ? (
          <div className="ly-success">
            <strong>Terima kasih.</strong>
            <span>Laporan Anda berhasil dikirim.</span>
          </div>
        ) : active ? (
          <div className="ly-sharePanel">
            <div className="ly-modalBody">
              {active.questions.map((q, i) => renderQuestion(q, i))}
            </div>
            <div className="ly-modalFoot">
              <button className="ly-btn" onClick={submitSurvey} disabled={submitting}>
                {submitting ? "Mengirim..." : "Kirim Laporan"}
              </button>
            </div>
          </div>
        ) : (
          <div className="ly-empty">Survey tidak ditemukan.</div>
        )}
      </div>
    );
  }

  return (
    <div className="lapor-yuk">
      <div className="ly-head">
        <div>
          <h1>Lapor Yuk!</h1>
          <p className="muted">Pilih survey yang sudah dipublish, lalu isi laporan Anda.</p>
        </div>
        {assistSelector}
      </div>

      {published.length === 0 ? (
        <div className="ly-empty">Belum ada survey yang tersedia untuk akun Anda.</div>
      ) : (
        <div className="ly-grid">
          {published.map((sv) => (
            <div key={sv.id} className="ly-card">
              <div className="ly-cardTitle">{sv.title}</div>
              {sv.description ? <div className="ly-cardDesc">{sv.description}</div> : null}
              <div className="ly-cardMeta">
                Dibuat: {sv.published_at ? new Date(sv.published_at).toLocaleString() : "-"}
                {typeof sv.version !== "undefined" ? ` - v${sv.version}` : ""}
              </div>
              <div className="ly-cardActions">
                <button className="ly-btn" onClick={() => openSurvey(sv)}>
                  Isi Survey
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal isi survey */}
      {active && (
        <div className="ly-modal" onClick={closeSurvey}>
          <div className="ly-modalContent" onClick={(e) => e.stopPropagation()}>
            <div className="ly-modalHead">
              <div>
                <div className="ly-modalTitle">{active.survey?.title || "Survey"}</div>
                {active.survey?.description ? (
                  <div className="ly-modalDesc">{active.survey.description}</div>
                ) : null}
              </div>
              <button className="ly-close" onClick={closeSurvey}>
                x
              </button>
            </div>

            <div className="ly-modalBody">
              {active.questions.map((q, i) => renderQuestion(q, i))}
            </div>

            <div className="ly-modalFoot">
              <button className="ly-btnSoft" onClick={closeSurvey} disabled={submitting}>
                Batal
              </button>
              <button className="ly-btn" onClick={submitSurvey} disabled={submitting}>
                {submitting ? "Mengirim..." : "Kirim Laporan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
